import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { noop } from 'shell/utils/noop';
import { ApplicantFormVM, CertificationFormVM } from 'civil-status/view-models/document-birth-form';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { DatePicker } from '@mui/lab';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { transformerPersonGeneralityList } from './transformer-anpr';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ItemVM } from 'shell/view-models/item';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import Table from 'shell/components/shared/table';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { PmiButton } from 'shell/components/shared/button';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { headCells } from './accountholder/constants';

const url = `${__PERSON_NEW_API_URL__}/persona/ricerca-certificati`;

const CertificationComponent = (): React.ReactElement => {
  const formContext = useFormikContext<CertificationFormVM>();

  const errorsApplicant = (formContext.errors.applicant as FormikErrors<ApplicantFormVM>) || {};

  const touchedApplicant =
    (formContext.touched.applicant as unknown as FormikTouched<ApplicantFormVM>) || {};

  const onChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('applicant', {
        ...(formContext.values.applicant || {}),
        [key]: value,
      });
    },
    [formContext.setFieldValue, formContext.values.applicant]
  );

  const onChanges = useCallback(
    (valuesNew) => {
      formContext.setFieldValue('applicant', {
        ...(formContext.values.applicant || {}),
        ...(valuesNew || {}),
      });
    },
    [formContext.setFieldValue, formContext.values.applicant]
  );

  const { userProfile } = useUserProfile();

  const [visibilityTable, setVisibilityTable] = useState(false);

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>(
    url,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>(
    url,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const showTooltip = useMemo(() => {
    if (
      formContext.values?.applicant?.fiscalCode ||
      (formContext.values?.applicant?.name && formContext.values?.applicant?.surname)
    ) {
      if (errorsApplicant?.searchApplicant) {
        return true;
      }
      return false;
    }

    if (
      !formContext.values?.applicant?.fiscalCode ||
      (!formContext.values?.applicant?.name && !formContext.values?.applicant?.surname)
    ) {
      return true;
    }

    return false;
  }, [formContext.values?.applicant, errorsApplicant.searchApplicant]);

  const tooltipMessage = useMemo(() => {
    if (
      formContext.values?.applicant?.fiscalCode ||
      (formContext.values?.applicant?.name && formContext.values?.applicant?.surname)
    ) {
      if (errorsApplicant?.searchApplicant) {
        return errorsApplicant?.searchApplicant;
      }
      return;
    }
    if (
      !formContext.values?.applicant?.fiscalCode ||
      (!formContext.values?.applicant?.name && !formContext.values?.applicant?.surname)
    ) {
      return 'Inserire codice fiscale o cognome e nome';
    }
  }, [formContext.values?.applicant, errorsApplicant?.searchApplicant]);

  const handleSearch = useEventCallback(() => {
    setVisibilityTable(false);

    if (
      !formContext.values?.applicant?.fiscalCode &&
      !(formContext.values?.applicant?.name && formContext.values?.applicant?.surname)
    ) {
      return;
    }

    const data = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: formContext.values?.applicant?.fiscalCode,
        name: formContext.values?.applicant?.name,
        surname: formContext.values?.applicant?.surname,
        withoutName: formContext.values?.applicant?.withoutName,
        withoutSurname: formContext.values?.applicant?.withoutSurname,
        gender: formContext.values?.applicant?.gender,
        birthday: formContext.values?.applicant?.birthDate,
        birthdayChoice: formContext.values?.applicant?.birthChoiceDate,
        isForeign: formContext.values?.applicant?.isForeign,
        foreignMunicipality: formContext.values?.applicant?.foreignMunicipality,
        foreignStateBirth: formContext.values?.applicant?.foreignState,
        municipalityOfBirth: formContext.values?.applicant?.municipality,
        provinceOfBirth: formContext.values?.applicant?.municipality?.province || undefined,
        // se feature auth anpr === 2
        isNationalResearch: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? true : false,
        municipalityOfResidence:
          userProfile?.datiLogin?.profile?.feature?.anpr === '1'
            ? {
                id: userProfile?.datiLogin?.codiceIstat || '',
                description: userProfile?.datiLogin?.nomeComune || '',
                province: userProfile?.datiLogin?.siglaProvincia || '',
              }
            : undefined,
      },
      identityCard: {
        number: formContext.values?.applicant?.documentNumber,
        // TODO data rilascio
      },
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificationFormVM>;

      if (castedResponse?.errors?.length > 0) {
        window.scrollTo(0, 0);
        setVisibility({});
        return;
      }

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        formContext.setValues({
          ...formContext.values,
          applicant: {
            ...(formContext.values.applicant || {}),
            ...element.applicant,
            searchApplicant: true,
          },
        });
        window.scrollTo(0, 0);
        return;
      }
      setVisibilityTable(true);
      resetPagination();
    });
  });

  const handleClickSelect = useEventCallback((person: CertificationFormVM) => {
    (
      getPerson({
        data: {
          criteriRicerca: {
            codiceFiscale: person.applicant?.fiscalCode || undefined,
            idSchedaSoggettoANPR: person.applicant?.idSubjectAnpr || undefined,
          },
          datiRichiesta: {
            comuneResidenza:
              userProfile?.datiLogin?.profile?.feature?.anpr === '1'
                ? {
                    codiceIstat: userProfile?.datiLogin?.codiceIstat,
                    nomeComune: userProfile?.datiLogin?.nomeComune,
                    siglaProvinciaIstat: userProfile?.datiLogin?.siglaProvincia,
                  }
                : undefined,
            ricercaNazionale: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? '1' : '0',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificationFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificationFormVM>;
      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        formContext.setValues({
          ...formContext.values,
          applicant: {
            ...(formContext.values.applicant || {}),
            ...element.applicant,
            searchApplicant: true,
          },
        });
        window.scrollTo(0, 0);
        setVisibilityTable(false);
      }
      resetPagination();
    });
  });

  const castedAnprData = anpr.data as AnprVM<CertificationFormVM>;

  useEffect(() => {
    if (castedAnprData?.persons?.length > 1) {
      onChange(
        'dataPersons',
        castedAnprData?.persons?.map((person) => ({
          fiscalCode: person.applicant?.fiscalCode || '',
          surname: person.applicant?.surname || '',
          name: person.applicant?.name || '',
          gender: person.applicant?.gender || '',
          birthday: person.applicant?.birthDate?.toFormat('dd/MM/yyyy') || '',
          birthplace: person.applicant?.birthplace,
          residence: person.applicant?.residence,
          state: person.applicant?.state,
          anpr: person.applicant?.idSubjectAnpr,
          action: (
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              onClick={() => handleClickSelect(person)}
            >
              Seleziona
            </PmiButton>
          ),
        })) || []
      );
    }
  }, [castedAnprData?.persons, handleClickSelect]);

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, anpr.data]);

  const onChangeGender = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('gender', event.target.value);
  });

  const onChangeChooseDate = useEventCallback(
    (date: DateTime | null, choice?: Array<DatePickerView>) => {
      formContext.setValues({
        ...formContext.values,
        applicant: {
          ...(formContext.values.applicant || {}),
          birthDate: date,
          birthChoiceDate: choice,
        },
      });
    }
  );

  const onChangeMunicipality = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('municipality', municipality);
  });

  const onChangeForeignMunicipality = useEventCallback(
    (foreignMunicipality: MunicipalityAnprVM | null) => {
      onChange('foreignMunicipality', foreignMunicipality);
    }
  );

  const onChangeMunicipalityDocument = useEventCallback(
    (municipalityDocument: MunicipalityAnprVM | null) => {
      onChange('municipalityDocument', municipalityDocument);
    }
  );

  const onChangeRadio = useEventCallback(() => {
    formContext.setValues({
      ...(formContext.values || {}),
      applicant: {
        ...(formContext.values.applicant || {}),
        municipality: null,
        foreignState: null,
        foreignMunicipality: null,
        isForeign: !formContext.values?.applicant?.isForeign,
      },
    });
  });

  const onChangeRadioDocument = useEventCallback(() => {
    formContext.setValues({
      ...(formContext.values || {}),
      applicant: {
        ...(formContext.values?.applicant || {}),
        municipalityDocument: null,
        foreignStateDocument: '',
        isForeignDocument: !formContext.values?.applicant?.isForeignDocument,
      },
    });
  });

  const onChangeForeignState = useEventCallback(
    (foreignState: ForeignStateVM | null | undefined) => {
      onChange('foreignState', foreignState);
    }
  );

  return (
    <Fragment>
      {visibilityTable || errorMessage.visibility ? (
        <div className="pmi-pt-2">
          <SearchMessages
            type={MessageType.Error}
            title={errorMessage.title || ''}
            messages={errorMessage.messages || []}
            visibility={errorMessage.visibility || false}
            onChangeVisibility={onVisibility}
          />
          <SearchMessages
            type={MessageType.Warning}
            title={`Ci sono ${castedAnprData?.warnings?.length || ''} warning`}
            messages={castedAnprData?.warnings || []}
            visibility={
              visibility.warning !== undefined
                ? visibility.warning
                : (castedAnprData?.warnings || [])?.length > 0
            }
            onChangeVisibility={onVisibility}
          />
        </div>
      ) : undefined}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Codice fiscale"
            aria-label="Codice fiscale"
            name="fiscalCode"
            id="fiscalCode"
            fullWidth
            value={formContext.values?.applicant?.fiscalCode?.toUpperCase() || ''}
            onChange={(event) => onChange('fiscalCode', event.target.value)}
            inputProps={{
              maxLength: '16',
            }}
            error={touchedApplicant?.fiscalCode && Boolean(errorsApplicant?.fiscalCode)}
            helperText={errorsApplicant?.fiscalCode}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Cognome *"
            aria-label="Cognome"
            id="surname"
            fullWidth
            value={formContext.values?.applicant?.surname || ''}
            onChange={(event) => onChange('surname', event.target.value)}
            error={touchedApplicant?.surname && Boolean(errorsApplicant?.surname)}
            helperText={errorsApplicant?.surname}
            disabled={formContext.values?.applicant?.withoutSurname}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname"
                checked={formContext.values?.applicant?.withoutSurname || false}
                onChange={() => {
                  onChanges({
                    withoutSurname: !formContext.values?.applicant?.withoutSurname,
                    surname: '',
                  });
                }}
                inputProps={{ 'aria-label': 'senza cognome' }}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Nome *"
            aria-label="Nome"
            id="name"
            fullWidth
            value={formContext.values?.applicant?.name || ''}
            onChange={(event) => onChange('name', event.target.value)}
            error={touchedApplicant?.name && Boolean(errorsApplicant?.name)}
            helperText={errorsApplicant?.name}
            disabled={formContext.values?.applicant?.withoutName}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName"
                checked={formContext.values?.applicant?.withoutName || false}
                onChange={() => {
                  onChanges({
                    withoutName: !formContext.values?.applicant?.withoutName,
                    name: '',
                  });
                }}
                inputProps={{ 'aria-label': 'senza nome' }}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ListGender
            value={formContext.values?.applicant?.gender || ''}
            onChange={onChangeGender}
            required
            error={touchedApplicant?.gender && Boolean(errorsApplicant?.gender)}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12} container spacing={2} alignItems="center">
            <ChooseDate
              label="Data di nascita"
              selected={formContext.values?.applicant?.birthDate || null}
              onChange={onChangeChooseDate}
              required
              choice={formContext.values?.applicant?.birthChoiceDate}
              helperText={errorsApplicant?.birthDate}
              error={touchedApplicant?.birthDate && Boolean(errorsApplicant?.birthDate)}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Nascita"
              id="isForeign"
              defaultValue={false}
              value={formContext.values?.applicant?.isForeign || false}
              onChange={() => onChangeRadio()}
            >
              <FormControlLabel value="false" control={<Radio />} label="Nascita Italia" />
              <FormControlLabel value="true" control={<Radio />} label="Nascita Estero" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {formContext.values?.applicant?.isForeign ? (
          <Fragment>
            <Grid item xs={12} md={4}>
              <ForeignStateList
                value={formContext.values?.applicant?.foreignState || null}
                onChange={onChangeForeignState}
                label="Stato estero di nascita"
                id="foreignStateBirth"
                required
                error={touchedApplicant?.foreignState && Boolean(errorsApplicant?.foreignState)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MunicipalityForeignList
                value={formContext.values?.applicant?.foreignMunicipality || null}
                onChange={onChangeForeignMunicipality}
                label="Comune estero *"
                id="foreignMunicipality"
                disabled={!formContext.values?.applicant?.foreignState}
                foreignStateIstatCode={formContext.values?.applicant?.foreignState?.id}
                error={
                  touchedApplicant?.foreignMunicipality &&
                  Boolean(errorsApplicant?.foreignMunicipality)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Provincia o Contea"
                label="Provincia/Contea"
                id="foreignProvince"
                name="foreignProvince"
                fullWidth
                type="text"
                disabled
                variant="outlined"
                value={formContext.values?.applicant?.foreignMunicipality?.province || ''}
              />
            </Grid>
          </Fragment>
        ) : (
          <Fragment>
            <Grid item xs={12} md={4}>
              <MunicipalityList
                label="Comune"
                fromDate={formContext.values?.applicant?.birthDate}
                required
                value={formContext.values?.applicant?.municipality || null}
                onChange={onChangeMunicipality}
                error={touchedApplicant?.municipality && Boolean(errorsApplicant?.municipality)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                label="Provincia"
                aria-label="Provincia"
                id="province"
                name="province"
                fullWidth
                onChange={noop}
                value={formContext.values?.applicant?.municipality?.province || ''}
                disabled
              />
            </Grid>
          </Fragment>
        )}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <PmiTitleDivider>Documento di riconoscimento</PmiTitleDivider>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              id="isForeignDocument"
              defaultValue={false}
              value={formContext.values?.applicant?.isForeignDocument || false}
              onChange={() => onChangeRadioDocument()}
            >
              <FormControlLabel value="false" control={<Radio />} label="Carta d'identità" />
              <FormControlLabel value="true" control={<Radio />} label="Altro" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Numero *"
            aria-label="Numero"
            fullWidth
            id="documentNumber"
            inputProps={{
              maxLength: '50',
            }}
            onChange={(event) => onChange('documentNumber', event.target.value)}
            value={formContext.values?.applicant?.documentNumber || ''}
            helperText={errorsApplicant?.documentNumber}
            error={touchedApplicant?.documentNumber && Boolean(errorsApplicant?.documentNumber)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data rilascio *"
            aria-label="Data rilascio"
            value={formContext.values?.applicant?.documentIssuedDate || null}
            onChange={(value) => onChange('documentIssuedDate', value)}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="documentIssuedDate"
                fullWidth
                error={
                  touchedApplicant?.documentIssuedDate &&
                  Boolean(errorsApplicant?.documentIssuedDate)
                }
                helperText={errorsApplicant?.documentIssuedDate}
              />
            )}
          />
        </Grid>
        <Grid item container spacing={2} alignItems="center">
          {formContext.values?.applicant?.isForeignDocument ? (
            <>
              <Grid item xs={12} md={7}>
                <PmiTextField
                  label="Ente/Luogo Rilascio *"
                  aria-label="Ente Luogo Rilascio"
                  id="release-location"
                  fullWidth
                  onChange={(event) => {
                    onChange('foreignStateDocument', event?.target.value);
                  }}
                  inputProps={{
                    maxLength: '120',
                  }}
                  value={formContext.values?.applicant?.foreignStateDocument || ''}
                  helperText={errorsApplicant.foreignStateDocument}
                  error={
                    touchedApplicant?.foreignStateDocument &&
                    Boolean(errorsApplicant?.foreignStateDocument)
                  }
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={4}>
                <MunicipalityList
                  label="Comune"
                  value={formContext.values?.applicant?.municipalityDocument || null}
                  onChange={onChangeMunicipalityDocument}
                  id="municipalityDocument"
                  fromDate={formContext.values?.applicant?.documentIssuedDate}
                  required
                  error={
                    touchedApplicant?.municipalityDocument &&
                    Boolean(errorsApplicant?.municipalityDocument)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  label="Provincia"
                  aria-label="Provincia"
                  id="documentIssuedProvinceIT"
                  name="documentIssuedProvinceIT"
                  fullWidth
                  onChange={noop}
                  value={formContext.values?.applicant?.municipalityDocument?.province || ''}
                  disabled
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
          <Grid item xs={12} md={2}>
            <PmiTooltipError show={showTooltip} message={tooltipMessage}>
              <span>
                <PmiButton
                  variant="outlined"
                  color="cdmBrandThird"
                  fullWidth
                  onClick={handleSearch}
                >
                  Cerca
                </PmiButton>
              </span>
            </PmiTooltipError>
          </Grid>
        </Grid>
        {(formContext.values?.applicant?.dataPersons || []).length > 1 && visibilityTable ? (
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista Persone"
              page={paginationState.page}
              rowsPerPage={paginationState.rowsPerPage}
              body={formContext.values?.applicant?.dataPersons || []}
              count={(formContext.values?.applicant?.dataPersons || []).length}
              head={headCells}
              onSort={noop}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onVisibilityPress={noop}
              orders={{}}
              isNotLayoutFixed
              noSort
              hideButtons
            />
          </Grid>
        ) : undefined}
      </Grid>
    </Fragment>
  );
};

CertificationComponent.displayName = 'Form generality component';

const CertificationForm = memo(CertificationComponent);

export default CertificationForm;
